// Lieferantengutschrift – Belegart 'supplier_credit_note' (EG-), AP8b: Der Lieferant schreibt uns etwas gut – Ware,
// die zurückging (Warenrücknahme, auch wenn sie auf seiner Rechnung mit negativer Menge stand), einen Preisnachlass,
// Pfand oder Leergut, einen Zuschlag. Mit der Freigabe („Festschreiben“) mindert ein Guthaben-Posten die
// Verbindlichkeiten beim Lieferanten; die angeforderte Gutschrift der bezogenen Eingangsrechnung gilt als eingegangen.
//
//   In Prüfung (Entwurf) → Freigegeben (festgeschrieben, Guthaben-Posten) → Storniert
//
// Der Einkauf bucht keinen Bestand: Ware, die zurück zum Lieferanten geht, bucht der Bestand (Korrekturbuchung bzw.
// eine künftige Rücksendung) – die Gutschrift ist der finanzielle Teil.
import { PERMISSION_COSTS_READ, defineContract, defineEvent } from '@shared/contract'
import { defineDocumentContracts, zDocumentView } from '@shared/document'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { CREDIT_NOTE_TYPE, zDay } from './common'
import { zCaptureExtraLine, zCaptureItemLine } from './invoice'

const PERMISSIONS = {
  read: 'purchasing.invoice.read',
  write: 'purchasing.invoice.check',
  post: 'purchasing.invoice.check',
  reverse: 'purchasing.invoice.check',
} as const

export const creditNote = defineDocumentContracts({
  module: 'purchasing',
  entity: 'creditNote',
  documentType: CREDIT_NOTE_TYPE,
  title: 'Lieferantengutschrift',
  titlePlural: 'Lieferantengutschriften',
  permissions: PERMISSIONS,
  keywords: ['Gutschrift vom Lieferanten', 'Lieferantengutschrift', 'Warenrücknahme', 'Rücksendung gutgeschrieben', 'Preisnachlass', 'EG'],
})

export const creditNoteGet = creditNote.get
export const creditNoteList = creditNote.list
export const creditNoteSaveDraft = creditNote.saveDraft
/** Freigeben: Guthaben-Posten beim Lieferanten */
export const creditNotePost = creditNote.post
export const creditNoteReverse = creditNote.reverse
export const creditNoteTransition = creditNote.transition
export const creditNotePrint = creditNote.print
export const creditNoteDiscard = creditNote.discard

/** Warum eine Artikelzeile gutgeschrieben wird: Ware zurück (mit dem Pfand des Gebindes) oder Preisnachlass (ohne Pfand) */
export const CREDIT_REASONS = ['return', 'price'] as const
export type CreditReason = (typeof CREDIT_REASONS)[number]

export const zCreditNoteLine = zCaptureItemLine.extend({
  /** return = Warenrücknahme (Pfand des Gebindes wird mit erstattet) · price = Preisnachlass auf die Ware (ohne Pfand) */
  reason: z.enum(CREDIT_REASONS, { error: 'Bitte wählen Sie: Warenrücknahme oder Preisnachlass.' }).default('return'),
})
export type CreditNoteLine = z.output<typeof zCreditNoteLine>

export const creditNoteCapture = defineContract({
  name: 'purchasing.creditNote.capture',
  title: 'Lieferantengutschrift erfassen',
  description:
    'Erfasst die Gutschrift eines Lieferanten mit ihrer Nummer und ihrem Datum: zurückgenommene Ware, Preisnachlässe, Gebindewerte, Zuschläge – wahlweise zu einer unserer Eingangsrechnungen.',
  llm: {
    description:
      'Erfasst eine Lieferantengutschrift als Entwurf („In Prüfung“): Nummer und Datum des Lieferanten, Standort, ' +
      'gutgeschriebene Artikel (`lines`: Menge als Betrag, Preis netto; `reason` return = Ware zurück samt Gebindewert, ' +
      'price = Preisnachlass ohne Gebindewert), Zusatzzeilen (`extraLines`: Gebindewerte, zurückgegebene Gebinde, ' +
      'Zuschläge, Abzüge). `invoiceId` nennt ' +
      'die Eingangsrechnung, die sie mindert. Eine Warenrücknahme, die auf der Rechnung des Lieferanten mit negativer ' +
      'Menge steht, wird hier mit positiver Menge erfasst. Gebucht ist nichts; Freigabe: purchasing.creditNote.post.',
    keywords: ['Gutschrift erfassen', 'Lieferantengutschrift', 'Gutschrift vom Lieferanten', 'Warenrücknahme', 'Rücksendung'],
  },
  input: z
    .strictObject({
      supplierId: zId,
      locationId: zId,
      /** Nummer der Gutschrift beim Lieferanten */
      supplierCreditNoteNo: z
        .string({ error: 'Bitte die Nummer der Gutschrift des Lieferanten eingeben.' })
        .trim()
        .min(1, { error: 'Bitte die Nummer der Gutschrift des Lieferanten eingeben.' })
        .max(120),
      creditNoteDate: zDay,
      /** unsere freigegebene Eingangsrechnung desselben Lieferanten, die die Gutschrift mindert */
      invoiceId: zId.optional(),
      lines: z.array(zCreditNoteLine).max(200, { error: 'Höchstens 200 Artikelzeilen.' }).default([]),
      /** Rabatt auf den gutgeschriebenen Warenwert (mindert die Gutschrift) in Basispunkten */
      headerDiscountBp: z.int().min(0).max(10_000).optional(),
      extraLines: z.array(zCaptureExtraLine).max(100, { error: 'Höchstens 100 Zusatzzeilen.' }).optional(),
      notes: z.string().trim().max(2000).nullish(),
    })
    .refine((input) => input.lines.length + (input.extraLines?.length ?? 0) > 0, { error: 'Die Gutschrift enthält keine Position.', path: ['lines'] }),
  output: zDocumentView,
  permission: PERMISSIONS.write,
  risk: 'write',
  scope: (input) => [input.locationId],
  limits: { maxItems: 300, itemCount: (input) => input.lines.length + (input.extraLines?.length ?? 0) },
  fieldPermissions: { 'lines.*.costCents': PERMISSION_COSTS_READ },
})

/** Eine Lieferantengutschrift ist freigegeben – der Guthaben-Posten existiert (Abnehmer: Finanzen). */
export const creditNoteReleased = defineEvent({
  name: 'purchasing.creditNote.released',
  title: 'Lieferantengutschrift freigegeben',
  payload: z.object({
    documentId: zId,
    number: z.string(),
    supplierId: zId,
    locationId: zId,
    /** bezogene Eingangsrechnung (null = ohne Bezug) */
    invoiceId: zId.nullable(),
    openItemId: zId.nullable(),
    /** gutgeschriebener Betrag brutto (positiv) */
    grossCents: z.int(),
  }),
})
